import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Save, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { getHonorariumTiers, updateHonorariumTiers } from '@/lib/api'
import type { HonorariumTier } from '@/types/domain'

interface HonorariumTiersConfigProps {
    trainerId: string
}

export function HonorariumTiersConfig({ trainerId }: HonorariumTiersConfigProps) {
    const queryClient = useQueryClient()
    const [localTiers, setLocalTiers] = useState<Omit<HonorariumTier, 'id'>[]>([])
    const [isEditing, setIsEditing] = useState(false)

    const { data: tiers, isLoading } = useQuery({
        queryKey: ['honorarium-tiers', trainerId],
        queryFn: async () => {
            const data = await getHonorariumTiers(trainerId)
            // Only set local tiers once when data is loaded and not already editing
            if (!isEditing) {
                setLocalTiers(data.map(({ id, ...rest }) => ({ ...rest } as any)))
            }
            return data
        },
    })

    const updateMutation = useMutation({
        mutationFn: (newTiers: Omit<HonorariumTier, 'id' | 'created_at' | 'updated_at'>[]) =>
            updateHonorariumTiers(trainerId, newTiers),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['honorarium-tiers', trainerId] })
            toast.success('Honorarsätze erfolgreich gespeichert')
            setIsEditing(false)
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`)
        },
    })

    const handleAddTier = () => {
        setLocalTiers([
            ...localTiers,
            {
                trainer_id: trainerId,
                min_participants: 0,
                max_participants: null,
                amount_cents: 0,
                is_default: false,
            },
        ])
        setIsEditing(true)
    }

    const handleRemoveTier = (index: number) => {
        const newTiers = [...localTiers]
        newTiers.splice(index, 1)
        setLocalTiers(newTiers)
        setIsEditing(true)
    }

    const handleUpdateTier = (index: number, field: keyof typeof localTiers[0], value: any) => {
        const newTiers = [...localTiers]
        newTiers[index] = { ...newTiers[index], [field]: value }
        setLocalTiers(newTiers)
        setIsEditing(true)
    }

    const handleSave = () => {
        // Basic validation: ensure amounts are > 0 and ranges make sense (optional)
        updateMutation.mutate(localTiers)
    }

    if (isLoading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        )
    }

    const hasSpecificTiers = tiers?.some(t => t.trainer_id === trainerId)

    return (
        <Card>
            <CardHeader>
                <CardTitle>Honorarsätze (Staffelung)</CardTitle>
                <CardDescription>
                    {hasSpecificTiers
                        ? 'Für diesen Trainer sind individuelle Honorarsätze konfiguriert.'
                        : 'Aktuell werden die globalen Standard-Honorarsätze verwendet.'}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Min. Teilnehmer</TableHead>
                            <TableHead>Max. Teilnehmer</TableHead>
                            <TableHead>Betrag (€)</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {localTiers.map((tier, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <Input
                                        type="number"
                                        value={tier.min_participants}
                                        onChange={(e) => handleUpdateTier(index, 'min_participants', parseInt(e.target.value) || 0)}
                                        className="w-24"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        placeholder="∞"
                                        value={tier.max_participants ?? ''}
                                        onChange={(e) => handleUpdateTier(index, 'max_participants', e.target.value ? parseInt(e.target.value) : null)}
                                        className="w-24"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={tier.amount_cents / 100}
                                        onChange={(e) => handleUpdateTier(index, 'amount_cents', Math.round(parseFloat(e.target.value) * 100) || 0)}
                                        className="w-32"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveTier(index)}
                                        className="text-destructive"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {localTiers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                                    Keine Sätze definiert.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <div className="flex justify-between items-center mt-4">
                    <Button variant="outline" size="sm" onClick={handleAddTier} className="gap-2">
                        <Plus className="h-4 w-4" /> Satz hinzufügen
                    </Button>

                    {isEditing && (
                        <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending} className="gap-2">
                            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Speichern
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
